import {
  Component,
  DestroyRef,
  EventEmitter,
  OnChanges,
  Output,
  SimpleChanges,
  effect,
  inject,
  input,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { PermissionsService } from '@coin/shared/auth/feature';
import { RequestTrackerInterceptorService } from '@coin/shared/core/data-access';
import { MercerDialogType } from '../../enums/mercer-dialog-type.enum';
import { MercerSupportHelper } from '../../helpers/mercer-support.helper';
import { MercerEvaluationMemberDetails } from '../../models/mercer-support-evaluation-member.model';
import { MercerPositionClassService } from '../../services/mercer-position-class.service';
import { MercerSupportDetailsService } from '../../services/mercer-support-details.service';
import { FormControl, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MercerState } from '../../../mercer/state/mercer.state';
import { MercerSupportState } from '../../../mercer-support/state/mercer-support.state';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelContent
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { PositionClassBarComponent } from '../position-class-bar/position-class-bar.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
  V2ButtonComponent,
  V2TextAreaComponent,
  V2NumberInputComponent,
  FormInfoMessageComponent
} from '@coin/shared/core/ui-storybook';

@Component({
  selector: 'customer-position-survey-result',
  templateUrl: './position-survey-result.component.html',
  styleUrls: ['./position-survey-result.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    MatTooltip,
    V2ButtonComponent,
    MatExpansionPanelContent,
    PositionClassBarComponent,
    V2TextAreaComponent,
    FormsModule,
    ReactiveFormsModule,
    V2NumberInputComponent,
    AsyncPipe,
    TranslatePipe,
    FormInfoMessageComponent
  ]
})
export class PositionSurveyResultComponent implements OnChanges {
  mercerPositionClassService = inject(MercerPositionClassService);
  private readonly mercerSupportState = inject(MercerSupportState);
  private readonly mercerState = inject(MercerState);
  mercerSupportDetailsService = inject(MercerSupportDetailsService);
  requestTracker = inject(RequestTrackerInterceptorService);
  private readonly permissionService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly memberDetails = input<MercerEvaluationMemberDetails>(undefined);
  readonly derivedPositionClass = input<number>(undefined);
  readonly isEditable = input<boolean>(undefined);
  readonly dialogType = input<MercerDialogType>(undefined);
  readonly isFinalizationAllowed = input<boolean>(undefined);
  readonly hideSaveButton = input(false);
  readonly isLocked = input<boolean>(undefined);
  readonly pcInputErrorMessage = input<string>(undefined);
  readonly positionClassValidationErrors = input<string[]>([]);
  readonly positionClassValidationWarnings = input<string[]>([]);

  @Output() public finalizePositionClass = new EventEmitter<void>();
  @Output() acceptProposedPositionClass = new EventEmitter<void>();
  @Output() changePositionClass = new EventEmitter<number>();

  public readonly tooltip = viewChild<MatTooltip>('tooltip');

  positionForm = new FormGroup({
    adjustedPositionClass: new FormControl(null, [Validators.required, this.positionClassValidator.bind(this)])
  });

  private positionClassValidator(): ValidationErrors | null {
    if (this.pcInputErrorMessage()) return { invalid: this.pcInputErrorMessage() };
    return null;
  }

  public get hidePositionClass(): boolean {
    return MercerSupportHelper.hidePositionClass(this.memberDetails());
  }

  public get isSentToHrAllowed(): boolean {
    return this.isEditable() && this.isFinalizationAllowed();
  }

  public get isPositionClassSavable(): boolean {
    return this.isEditable() && !!this.adjustedPositionClass && this.isSavePositionClassForManagersAllowed;
  }

  public get isChangeOrAcceptPCAllowed(): boolean {
    const memberDetails = this.memberDetails();
    if (MercerSupportHelper.isPositionRequested(memberDetails)) {
      return true;
    }

    if (MercerSupportHelper.isPositionRequestedForCBAdmin(memberDetails)) {
      return MercerSupportHelper.isAdmin(this.permissionService);
    }

    return false;
  }

  // Hide the position-class area in the manager-app if necessary
  public get isPositionClassVisible(): boolean {
    const dialogType = this.dialogType();
    const memberDetails = this.memberDetails();
    return (
      dialogType === MercerDialogType.Support ||
      MercerSupportHelper.isPositionSectionVisibleForGermanWithPartialEvaluation(
        memberDetails,
        dialogType,
        this.mercerState,
        this.mercerSupportState,
        this.derivedPositionClass()
      ) ||
      !memberDetails?.isPositionClassHidden
    );
  }

  public get isSavePositionClassForManagersAllowed(): boolean {
    const isDeviationReasonNeeded =
      this.adjustedPositionClass &&
      !this.memberDetails()?.deviationReason &&
      this.dialogType() !== MercerDialogType.Headcount;
    return (
      this.dialogType() === MercerDialogType.Support || (!this.isInvalidPcTooltipVisible && !isDeviationReasonNeeded)
    );
  }

  public adjustedPositionClass: number;
  public isExpanded = true;
  public mercerDialogType = MercerDialogType;
  public isInvalidPcTooltipVisible = false;
  public readonly maxPositionClass = MercerSupportHelper.maxPositionClass;

  constructor() {
    effect(() => {
      if (this.isEditable()) {
        this.positionForm.controls.adjustedPositionClass.enable();
      } else {
        this.positionForm.controls.adjustedPositionClass.disable();
      }
    });

    this.mercerPositionClassService.adjustedPositionClass$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(positionClass => {
        const tooltip = this.tooltip();
        const tooltipElement: HTMLElement = tooltip?.['_elementRef']?.nativeElement;
        if (tooltipElement?.contains(document.activeElement)) tooltip.show();
        this.isInvalidPcTooltipVisible = this.isManagerPositionClassOutOfRange(positionClass);
        this.adjustedPositionClass = positionClass;
        this.positionForm.controls.adjustedPositionClass.setValue(positionClass, { emitViewToModelChange: false });
        this.emitPermissionChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const newPositionClass = changes.memberDetails?.currentValue?.newPositionClass;

    if (
      changes.memberDetails?.previousValue?.newPositionClass !==
        changes.memberDetails?.currentValue?.newPositionClass ||
      changes.derivedPositionClass?.previousValue !== changes.derivedPositionClass?.currentValue ||
      (newPositionClass && this.adjustedPositionClass !== newPositionClass)
    ) {
      this.setAdjustedPositionClass();
    }

    if (changes.memberDetails?.previousValue?.id !== changes.memberDetails?.currentValue?.id) {
      this.isInvalidPcTooltipVisible = false;
      this.tooltip()?.hide();
    }

    this.emitPermissionChanges();
  }

  private setAdjustedPositionClass(): void {
    this.adjustedPositionClass = this.hidePositionClass ? null : this.memberDetails()?.newPositionClass;
    this.positionForm.controls.adjustedPositionClass.setValue(this.adjustedPositionClass);
    this.mercerPositionClassService.adjustedPositionClass$.next(this.adjustedPositionClass);
  }

  public updateAdjustedPositionClass(event: string): void {
    const newPositionClass = Number.parseInt(event);

    this.mercerPositionClassService.adjustedPositionClass$.next(newPositionClass);
    this.emitPermissionChanges();
  }

  private isManagerPositionClassOutOfRange(pc: number): boolean {
    return (
      (this.dialogType() === MercerDialogType.Manager || this.isHeadcountManager()) &&
      Math.abs(this.derivedPositionClass() - pc) > 1
    );
  }

  private isHeadcountManager(): boolean {
    return (
      this.dialogType() === MercerDialogType.Headcount &&
      !this.permissionService.isHeadcountApprovalAdmin() &&
      !this.permissionService.isHeadcountApprovalHr() &&
      !this.permissionService.isCAndB()
    );
  }

  public emitPermissionChanges(): void {
    this.mercerPositionClassService.isFinalizeForManagersAllowed$.next(this.isSavePositionClassForManagersAllowed);
    this.mercerPositionClassService.isSavePositionClassAllowed$.next(this.isPositionClassSavable);
  }
}
